import { useCallback, useContext, useState } from "react"

import { Link, useLocalSearchParams } from "expo-router"

import {
  AccountCollection,
  AccountData,
  ClientCollection,
  Link as HALLink,
  RecurringDepositoryTransaction,
  RecurringDepositoryTransactionCollection,
} from "wealthmode-frontend/backend"
import { Toggle } from "wealthmode-frontend/components"
import { Box, HStack, Text, VStack } from "wealthmode-frontend/gluestack/ui"
import { formatRecurringAmount, formatRecurringDay } from "wealthmode-frontend/util"

import { DataTable, IDLink } from "@/components"
import { BackendContext } from "@/context"

const STATUS_OPTIONS = [
  { id: "active", text: "Active" },
  { id: "expired", text: "Expired" },
]

const STATUS_WHERE: Record<string, string> = {
  active: "eq(end_date,null)",
  expired: "neq(end_date,null)",
}

export default function RecurringDepositoryTransactions() {
  const { client_id, account_id } = useLocalSearchParams<{
    client_id: string
    account_id: string
  }>()

  const backend = useContext(BackendContext)

  const [status, setStatus] = useState("active")

  const load = useCallback(async () => {
    const clients = backend.collection("clients") as ClientCollection
    const client = clients.model(client_id)
    await client.fetch({ reload: true })

    const accounts = client.link("accounts")?.resolve() as AccountCollection
    const account = accounts.model(account_id)
    await account.fetch({ reload: true })

    const data = account.embed("data") as AccountData
    const link = data.link("recurring_depository_transactions")
    if (link == null || link.type == null) {
      return undefined
    }

    // A Link memoizes what it resolves to, so switching status needs a
    // collection of its own. The clause goes in the URL rather than in fetch
    // options so reloads and pagination keep it.
    const href = new URL(link.href.toString())
    href.searchParams.set("where", STATUS_WHERE[status])

    const transactions = new HALLink(href, link.type).resolve() as RecurringDepositoryTransactionCollection
    await transactions.fetch({ reload: true })

    return transactions
  }, [backend, client_id, account_id, status])

  return (
    <Box>
      <VStack className="flex-1 gap-4">
        <HStack className="items-center justify-end">
          <Toggle
            options={STATUS_OPTIONS}
            value={status}
            onChange={(option) => setStatus(option.id)}
          />
        </HStack>

        <HStack>
          <Box className="flex-1">
            <DataTable
              title={"Recurring Depository Transactions"}
              load={load}
              header={[
                { id: "id", title: "ID", shrink: true },
                { id: "frequency", title: "Frequency" },
                { id: "interval", title: "Interval", shrink: true },
                { id: "day", title: "Day", shrink: true },
                { id: "start_date", title: "Start" },
                { id: "end_date", title: "End" },
                { id: "confidence", title: "Confidence" },
                { id: "transaction_type", title: "Type" },
                { id: "amount_type", title: "Amount Type" },
                { id: "amount", title: "Amount" },
                { id: "occurrences", title: "Occurrences", center: true, noTruncate: true },
              ]}
              render={(model: RecurringDepositoryTransaction, field: string) => {
                switch (field) {
                  case "id":
                    return (
                      <IDLink
                        href={`/clients/${client_id}/accounts/${account_id}/recurring_depository_transactions/${model.attributes.id}`}
                        id={model.attributes.id}
                      />
                    )

                  case "day":
                    return formatRecurringDay(model.attributes)

                  case "amount":
                    return formatRecurringAmount(model.attributes)

                  case "occurrences":
                    return (
                      <Link
                        href={`/clients/${client_id}/accounts/${account_id}/recurring_depository_transactions/${model.attributes.id}/occurrences`}
                      >
                        <Text className="text-markdown-link hover:opacity-80 underline whitespace-nowrap">View</Text>
                      </Link>
                    )
                }
                return model.get(field)
              }}
            />
          </Box>
        </HStack>
      </VStack>
    </Box>
  )
}
